import { useState } from "react"
import { observer } from "mobx-react-lite"
import { useNavigate } from "react-router-dom"

import { TooltipProvider } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { CL_PRODUCT_COPY } from "@/domains/manual-cl-page/cl-product-copy"
import { CREATE_MANUAL_CL_V1_BREADCRUMBS } from "@/domains/thick-v1/breadcrumbs"
import { SwapTokenDialog } from "@/domains/tokens/components/token-dialog/swap-token-dialog"
import { useNativeTokenStore } from "@/hooks/use-native-token-store"

import { ManualClPageLayout } from "../manual-cl-page-layout"
import { CreatePoolCardView } from "./create-pool-card-view"

/**
 * Composite component for create pool page
 * Connects all view components to CreatePoolStore
 * Handles user interactions and delegates to store actions
 */
export const CreatePoolComposite = observer(() => {
  const scaleApp = useScaleApplication()
  const nativeTokenStore = useNativeTokenStore()
  const navigate = useNavigate()

  // Connected account is read from the wallet store rather than wagmi's
  // `useAccount` (forbidden in composites per architect.mdc).
  const account = scaleApp.Wallet.Connection.connectedAddress?.raw
  const store = scaleApp.ManualClV1.CreatePool

  const [isToken0DialogOpen, setIsToken0DialogOpen] = useState(false)
  const [isToken1DialogOpen, setIsToken1DialogOpen] = useState(false)

  // Get all tokens from repository
  const allTokens = scaleApp.Tokens.Repository.getAll()

  const handleCreatePool = async () => {
    if (account && store.canCreatePool) {
      const poolAddress = await store.createPool(account)
      if (poolAddress) {
        navigate(`/liquidity/cl/v1/${poolAddress}`)
      }
    }
  }

  const title = CL_PRODUCT_COPY.v1.createPoolTitle

  return (
    <TooltipProvider>
      <PageContainer>
        <MetaData title={title} />
        <PageTitle title={title} items={CREATE_MANUAL_CL_V1_BREADCRUMBS} />
        <ManualClPageLayout>
          <CreatePoolCardView
            token0={store.token0}
            token1={store.token1}
            onToken0Select={() => setIsToken0DialogOpen(true)}
            onToken1Select={() => setIsToken1DialogOpen(true)}
            feeTiers={store.availableFeeTiers}
            selectedFeeTier={store.selectedFeeTier}
            poolOptions={store.allPoolOptions}
            onFeeTierSelect={(feeTier) => store.setSelectedFeeTier(feeTier)}
            initialPrice={store.initialPrice}
            marketPrice={store.marketPrice}
            onPriceChange={(price) => store.setInitialPrice(price)}
            onUseMarketPrice={() => store.setUseMarketPrice(true)}
            useMarketPrice={store.useMarketPrice}
            isLoadingMarketPrice={store.marketPrice === undefined && store.hasBothTokens}
            canCreatePool={store.canCreatePool}
            isCreating={store.isCreating}
            creationError={store.creationError}
            onCreatePool={handleCreatePool}
          />
        </ManualClPageLayout>

        {/* Token Selection Dialogs */}
        <SwapTokenDialog
          isOpen={isToken0DialogOpen}
          tokens={allTokens}
          quickSelectTokens={scaleApp.Tokens.QuickSelect.tokens}
          governanceToken={{
            name: scaleApp.Tokens.governance.name,
          }}
          nativeToken={nativeTokenStore}
          wrappedNativeToken={scaleApp.Tokens.wrappedNative}
          showLocalAssets={true}
          onClose={() => setIsToken0DialogOpen(false)}
          onTokenSelect={(token) => {
            if (token) {
              store.setToken0(token)
            }
            setIsToken0DialogOpen(false)
          }}
        />

        <SwapTokenDialog
          isOpen={isToken1DialogOpen}
          tokens={allTokens}
          quickSelectTokens={scaleApp.Tokens.QuickSelect.tokens}
          governanceToken={{
            name: scaleApp.Tokens.governance.name,
          }}
          nativeToken={nativeTokenStore}
          wrappedNativeToken={scaleApp.Tokens.wrappedNative}
          showLocalAssets={true}
          onClose={() => setIsToken1DialogOpen(false)}
          onTokenSelect={(token) => {
            if (token) {
              store.setToken1(token)
            }
            setIsToken1DialogOpen(false)
          }}
        />
      </PageContainer>
    </TooltipProvider>
  )
})
